import { axiosInstance } from '../../../api/axiosConfig';
import type { PLVProduct, PLVOrder } from '../types/plv.types';
import env from '@/config/env';

const API_BASE_URL = env.PUBLIC_API_URL;

export function getImageUrl(imageKey: string): string {
  if (!imageKey) return '';
  if (imageKey.startsWith('http')) return imageKey;
  const encodedKey = encodeURIComponent(imageKey);
  return `${API_BASE_URL}/plv/files/${encodedKey}/image`;
}

export const plvApi = {
  getProducts: async (societyId: string): Promise<PLVProduct[]> => {
    const { data } = await axiosInstance.get<PLVProduct[]>('/plv/products', {
      params: { company_id: societyId },
    });
    return data;
  },

  getProduct: async (productId: number): Promise<PLVProduct> => {
    const { data } = await axiosInstance.get<PLVProduct>(`/plv/products/${productId}`);
    return data;
  },

  createOrder: async (orderData: {
    societyId: string;
    orderedBy: string;
    items: { productId: number; quantity: number }[];
    deliveryContactLastname?: string;
    deliveryContactFirstname?: string;
    deliveryContactEmail?: string;
    deliveryContactPhone?: string;
    deliveryCompanyName?: string;
    deliveryAddress?: string;
    deliveryZip?: string;
    deliveryCity?: string;
    emailNotification?: boolean;
  }): Promise<PLVOrder> => {
    const { data } = await axiosInstance.post<PLVOrder>('/plv/orders', orderData);
    return data;
  },

  getOrders: async (societyId: string): Promise<PLVOrder[]> => {
    const { data } = await axiosInstance.get<PLVOrder[]>('/plv/orders', {
      params: { company_id: societyId },
    });
    return data;
  },

  getOrder: async (orderId: number): Promise<PLVOrder> => {
    const { data } = await axiosInstance.get<PLVOrder>(`/plv/orders/${orderId}`);
    return data;
  },
};

export function formatDate(dateString: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).format(new Date(dateString));
}

export function getStatusInfo(status: PLVOrder['status']): { label: string; colorClass: string } {
  const statusMap: Record<PLVOrder['status'], { label: string; colorClass: string }> = {
    ORDERED: { label: 'Commandé', colorClass: 'tw-bg-blue-100 tw-text-blue-700' },
    PROCESSING: { label: 'En préparation', colorClass: 'tw-bg-yellow-100 tw-text-yellow-700' },
    SENT: { label: 'Expédié', colorClass: 'tw-bg-cyan-100 tw-text-cyan-700' },
    DELIVERED: { label: 'Livré', colorClass: 'tw-bg-green-100 tw-text-green-700' },
  };
  return statusMap[status] || { label: status, colorClass: 'tw-bg-gray-100 tw-text-gray-700' };
}

export function getTypeLabel(type: string): string {
  const typeMap: Record<string, string> = {
    carte: 'Carte',
    pack_carte: 'Pack cartes',
    plv: 'PLV',
  };
  return typeMap[type] || type.charAt(0).toUpperCase() + type.slice(1);
}
